import { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useCommsGate } from '@/components/CommsUpgradeDialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  ArrowLeft, 
  Plus, 
  FileText, 
  Link as LinkIcon, 
  Car,
  User,
  Calendar,
  DollarSign,
  Trash2,
  X,
  Wrench,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Camera,
  Printer,
  Mail,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import InvoiceForm from '@/components/InvoiceForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500'
};

const JOB_CARD_STATUS_COLORS = {
  pickup: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  waiting_for_parts: 'bg-orange-100 text-orange-700',
  blocked: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700'
};

const JOB_CARD_STATUS_LABELS = {
  pickup: 'Dropped-off',
  in_progress: 'In Progress',
  waiting_for_parts: 'Waiting for Parts',
  blocked: 'Sublet',
  completed: 'Completed'
};

const SERVICE_TYPES = [
  { value: 'service', label: 'General Service' },
  { value: 'rwc', label: 'RWC (Roadworthy)' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'aircon', label: 'Aircon' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'other', label: 'Other' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

// Memoized Line Item Row component to prevent focus loss on re-render
const LineItemRow = memo(function LineItemRow({ item, index, onUpdate, onRemove, showRemove }) {
  const [localDescription, setLocalDescription] = useState(item.description);
  const [localQuantity, setLocalQuantity] = useState(item.quantity);
  const [localUnitPrice, setLocalUnitPrice] = useState(item.unit_price);
  const [localDiscount, setLocalDiscount] = useState(item.discount_percent);

  // Sync local state when item prop changes (e.g., from service schedule selection)
  useEffect(() => {
    setLocalDescription(item.description);
    setLocalQuantity(item.quantity);
    setLocalUnitPrice(item.unit_price);
    setLocalDiscount(item.discount_percent);
  }, [item.description, item.quantity, item.unit_price, item.discount_percent]);

  const handleDescriptionBlur = () => {
    if (localDescription !== item.description) {
      onUpdate(index, 'description', localDescription);
    }
  };

  const handleQuantityBlur = () => {
    const value = parseInt(localQuantity) || 0;
    if (value !== item.quantity) {
      onUpdate(index, 'quantity', value);
    }
  };

  const handleUnitPriceBlur = () => {
    const value = parseFloat(localUnitPrice) || 0;
    if (value !== item.unit_price) {
      onUpdate(index, 'unit_price', value);
    }
  };

  const handleDiscountBlur = () => {
    const value = parseFloat(localDiscount) || 0;
    if (value !== item.discount_percent) {
      onUpdate(index, 'discount_percent', value);
    }
  };

  const itemTotal = (parseFloat(localQuantity) || 0) * (parseFloat(localUnitPrice) || 0) * (1 - (parseFloat(localDiscount) || 0) / 100);

  return (
    <div className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-3 rounded-lg">
      <div className="col-span-4">
        <Label className="text-xs">Description</Label>
        <Input
          value={localDescription}
          onChange={(e) => setLocalDescription(e.target.value)}
          onBlur={handleDescriptionBlur}
          placeholder="Service description"
          className="mt-1"
        />
      </div>
      <div className="col-span-2">
        <Label className="text-xs">Qty</Label>
        <Input
          type="number"
          value={localQuantity}
          onChange={(e) => setLocalQuantity(e.target.value)}
          onBlur={handleQuantityBlur}
          min="1"
          className="mt-1"
        />
      </div>
      <div className="col-span-2">
        <Label className="text-xs">Unit Price</Label>
        <Input
          type="number"
          value={localUnitPrice}
          onChange={(e) => setLocalUnitPrice(e.target.value)}
          onBlur={handleUnitPriceBlur}
          min="0"
          step="0.01"
          className="mt-1"
        />
      </div>
      <div className="col-span-2">
        <Label className="text-xs">Discount %</Label>
        <Input
          type="number"
          value={localDiscount}
          onChange={(e) => setLocalDiscount(e.target.value)}
          onBlur={handleDiscountBlur}
          min="0"
          max="100"
          className="mt-1"
        />
      </div>
      <div className="col-span-1 text-right">
        <Label className="text-xs">Total</Label>
        <p className="mt-1 h-10 flex items-center justify-end font-medium">
          ${itemTotal.toFixed(2)}
        </p>
      </div>
      <div className="col-span-1 flex justify-end">
        {showRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove(index)}
            className="text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
});

export default function VehicleDetails() {
  const { guardComms, commsDialog } = useCommsGate();
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [unlinkedInvoices, setUnlinkedInvoices] = useState([]);
  
  // Job Cards History state
  const [jobCards, setJobCards] = useState([]);
  const [jobCardsLoading, setJobCardsLoading] = useState(false);
  const [expandedJobCards, setExpandedJobCards] = useState({});
  const [jobCardsExpanded, setJobCardsExpanded] = useState(true);
  
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showJobCardDialog, setShowJobCardDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [mechanics, setMechanics] = useState([]);
  const [jobCardLoading, setJobCardLoading] = useState(false);
  
  // SMS Modal State
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedSmsTemplate, setSelectedSmsTemplate] = useState('');
  const [customSmsMessage, setCustomSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  
  // Service schedules state (Boyce's library)
  const [serviceSchedules, setServiceSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  
  const [jobCardForm, setJobCardForm] = useState({
    service_type: '',
    comments: '',
    priority: 'medium',
    assigned_to: '',
    scheduled_date: ''  // ISO date string for scheduling
  });
  
  const [invoiceForm, setInvoiceForm] = useState({
    service_type: '',
    odometer_reading: '',
    notes: '',
    status: 'draft',
    line_items: [{ id: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0, discount_percent: 0, gst_included: true }]
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    if (user && vehicleId) {
      fetchVehicle();
      fetchInvoices();
      fetchJobCards();
      fetchServiceSchedules();
    }
  }, [user, vehicleId]);

  const fetchVehicle = async () => {
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}${siteParams}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const vehicleData = await response.json();
        setVehicle(vehicleData);
        
        // Fetch customer details
        if (vehicleData.customer_id) {
          const customerResponse = await fetch(`${BACKEND_URL}/api/customers/${vehicleData.customer_id}${siteParams}`, {
            credentials: 'include'
          });
          if (customerResponse.ok) {
            setCustomer(await customerResponse.json());
          }
        }
      } else {
        toast.error('Vehicle not found');
        navigate(-1);
      }
    } catch (error) {
      console.error('Failed to fetch vehicle:', error);
      toast.error('Failed to load vehicle');
    }
  };

  const fetchInvoices = async () => {
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}/invoices${siteParams}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices);
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const fetchJobCards = async () => {
    setJobCardsLoading(true);
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}/job-cards${siteParams}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobCards(data.job_cards || []);
      }
    } catch (error) {
      console.error('Failed to fetch job cards:', error);
    } finally {
      setJobCardsLoading(false);
    }
  };

  const toggleJobCardExpanded = (jobCardId) => {
    setExpandedJobCards(prev => ({
      ...prev,
      [jobCardId]: !prev[jobCardId]
    }));
  };

  // Fetch service schedules for this vehicle (Boyce's library)
  const fetchServiceSchedules = async (odometer = null) => {
    if (!vehicleId) return;
    
    setSchedulesLoading(true);
    try {
      const siteParams = getSiteParams();
      const odometerParam = odometer ? `&odometer=${odometer}` : '';
      const response = await fetch(
        `${BACKEND_URL}/api/vehicles/${vehicleId}/recommended-services${siteParams}${odometerParam}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setServiceSchedules(data.schedules || []);
      }
    } catch (error) {
      console.error('Failed to fetch service schedules:', error);
    } finally {
      setSchedulesLoading(false);
    }
  };

  // Apply selected service schedule to line items
  const applyServiceSchedule = (scheduleId) => {
    const schedule = serviceSchedules.find(s => s.schedule_id === scheduleId);
    if (!schedule) {
      setSelectedSchedule(null);
      return;
    }
    
    setSelectedSchedule(schedule);
    
    // Update invoice form with schedule's line items (with unique IDs for stable keys)
    setInvoiceForm(prev => ({
      ...prev,
      service_type: schedule.service_name,
      line_items: schedule.line_items.map(item => ({
        id: crypto.randomUUID(),
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount_percent: item.discount_percent || 0,
        gst_included: item.gst_included !== false
      }))
    }));
    
    toast.success(`Applied ${schedule.service_name} - ${schedule.line_items.length} items added`);
  };

  const fetchUnlinkedInvoices = async () => {
    if (!customer) return;
    
    try {
      const siteParams = getSiteParams();
      const sep = siteParams ? '&' : '?';
      const response = await fetch(`${BACKEND_URL}/api/invoices/unlinked/list${siteParams}${sep}customer_id=${customer.customer_id}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnlinkedInvoices(data.invoices);
      }
    } catch (error) {
      console.error('Failed to fetch unlinked invoices:', error);
    }
  };

  const fetchMechanics = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/job-cards/mechanics`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMechanics(data);
      }
    } catch (error) {
      console.error('Failed to fetch mechanics:', error);
    }
  };

  const resetForm = () => {
    setInvoiceForm({
      service_type: '',
      odometer_reading: '',
      notes: '',
      status: 'draft',
      line_items: [{ description: '', quantity: 1, unit_price: 0, discount_percent: 0, gst_included: true }]
    });
  };

  const resetJobCardForm = () => {
    setJobCardForm({
      service_type: '',
      comments: '',
      priority: 'medium',
      assigned_to: '',
      scheduled_date: ''
    });
  };

  const handleCreateJobCard = async (e) => {
    e.preventDefault();
    
    if (!jobCardForm.service_type) {
      toast.error('Please select a service type');
      return;
    }
    
    if (!jobCardForm.scheduled_date) {
      toast.error('Please select a scheduled date');
      return;
    }
    
    setJobCardLoading(true);
    try {
      // Convert date string to ISO datetime for the scheduled date
      const scheduledDateTime = jobCardForm.scheduled_date 
        ? new Date(jobCardForm.scheduled_date + 'T09:00:00').toISOString()
        : null;
      
      const response = await fetch(`${BACKEND_URL}/api/job-cards/direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          customer_id: customer.customer_id,
          service_type: jobCardForm.service_type,
          comments: jobCardForm.comments || null,
          priority: jobCardForm.priority,
          assigned_to: jobCardForm.assigned_to || null,
          scheduled_date: scheduledDateTime
        })
      });
      
      if (response.ok) {
        const newJobCard = await response.json();
        const scheduledDate = new Date(jobCardForm.scheduled_date).toLocaleDateString('en-AU', {
          weekday: 'long',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
        toast.success(`Job Card ${newJobCard.job_card_number} scheduled for ${scheduledDate}`);
        setShowJobCardDialog(false);
        resetJobCardForm();
        // Navigate to job cards page
        navigate('/job-cards');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create job card' }));
        toast.error(errorData.detail || 'Failed to create job card');
      }
    } catch (error) {
      console.error('Failed to create job card:', error);
      toast.error('Failed to create job card');
    } finally {
      setJobCardLoading(false);
    }
  };

  const handleCreateInvoice = async (formData) => {
    try {
      const siteParams = getSiteParams();
      const payload = {
        vehicle_id: vehicleId,
        customer_id: customer.customer_id,
        service_type: formData.service_type || null,
        odometer_reading: formData.odometer_reading ? parseInt(formData.odometer_reading) : null,
        notes: formData.notes || null,
        status: formData.status,
        service_categories: formData.service_categories || [],  // Internal tracking categories for Super Admin
        line_items: formData.line_items.filter(item => item.description)
      };
      
      const response = await fetch(`${BACKEND_URL}/api/invoices${siteParams}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const newInvoice = await response.json();
        toast.success(`Invoice ${newInvoice.doc_no} created successfully`);
        fetchInvoices();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create invoice' }));
        toast.error(errorData.detail || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Failed to create invoice:', error);
      toast.error('Failed to create invoice');
    }
  };

  const handleUpdateInvoice = async (formData) => {
    try {
      const siteParams = getSiteParams();
      const payload = {
        service_type: formData.service_type || null,
        odometer_reading: formData.odometer_reading ? parseInt(formData.odometer_reading) : null,
        notes: formData.notes || null,
        status: formData.status,
        service_categories: formData.service_categories || [],  // Internal tracking categories for Super Admin
        line_items: formData.line_items.filter(item => item.description)
      };
      
      const response = await fetch(`${BACKEND_URL}/api/invoices/${editingInvoice.invoice_id}${siteParams}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Invoice updated successfully');
        setShowEditDialog(false);
        setEditingInvoice(null);
        fetchInvoices();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update invoice' }));
        toast.error(errorData.detail || 'Failed to update invoice');
      }
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast.error('Failed to update invoice');
    }
  };

  // Fetch SMS templates when modal opens
  const fetchSmsTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/marketing/templates?channel=sms`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSmsTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch SMS templates:', error);
    }
  };

  // Handle SMS send
  const handleSendSms = async () => {
    if (!editingInvoice) return;
    
    const template = smsTemplates.find(t => t.template_id === selectedSmsTemplate);
    const message = template ? 
      template.content
        .replace(/{customer_name}/g, customer?.display_name || customer?.first_name || 'Customer')
        .replace(/{vehicle_rego}/g, vehicle?.rego || '')
        .replace(/{invoice_total}/g, `$${editingInvoice.total?.toFixed(2) || '0.00'}`)
        .replace(/{invoice_id}/g, editingInvoice.doc_no || '')
      : customSmsMessage;

    if (!message.trim()) {
      toast.error('Please select a template or write a custom message');
      return;
    }

    setSendingSms(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${editingInvoice.invoice_id}/send-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message })
      });

      if (response.ok) {
        toast.success('SMS sent successfully');
        setShowSmsModal(false);
        setSelectedSmsTemplate('');
        setCustomSmsMessage('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Failed to send SMS:', error);
      toast.error('Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  // Open SMS modal and fetch templates
  useEffect(() => {
    if (showSmsModal) {
      fetchSmsTemplates();
    }
  }, [showSmsModal]);

  const handleLinkInvoice = async (invoiceId) => {
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/invoices/link-vehicle${siteParams}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          invoice_id: invoiceId,
          vehicle_id: vehicleId
        })
      });
      
      if (response.ok) {
        toast.success('Invoice linked successfully');
        setShowLinkDialog(false);
        fetchInvoices();
        fetchUnlinkedInvoices();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to link invoice' }));
        toast.error(errorData.detail || 'Failed to link invoice');
      }
    } catch (error) {
      console.error('Failed to link invoice:', error);
      toast.error('Failed to link invoice');
    }
  };

  const openEditDialog = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceForm({
      service_type: invoice.service_type || '',
      odometer_reading: invoice.odometer_reading ? invoice.odometer_reading.toString() : '',
      notes: invoice.notes || '',
      status: invoice.status || 'draft',
      line_items: invoice.line_items?.length > 0 
        ? invoice.line_items 
        : [{ description: '', quantity: 1, unit_price: 0, discount_percent: 0, gst_included: true }]
    });
    setShowEditDialog(true);
  };

  const addLineItem = useCallback(() => {
    setInvoiceForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { id: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0, discount_percent: 0, gst_included: true }]
    }));
  }, []);

  const removeLineItem = useCallback((index) => {
    setInvoiceForm(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  }, []);

  const updateLineItem = useCallback((index, field, value) => {
    setInvoiceForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  }, []);

  const calculateTotal = () => {
    return invoiceForm.line_items.reduce((sum, item) => {
      const subtotal = (item.quantity || 0) * (item.unit_price || 0);
      const discount = subtotal * ((item.discount_percent || 0) / 100);
      return sum + (subtotal - discount);
    }, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
  };

  if (loading || !vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const InvoiceFormContent = ({ onSubmit, submitText }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      {/* Service Schedule Selection - Boyce's Library */}
      {serviceSchedules.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <Label className="text-blue-800 font-semibold mb-2 block">
            Quick Service Selection (Based on {vehicle?.make} {vehicle?.model})
          </Label>
          <Select 
            value={selectedSchedule?.schedule_id || ''} 
            onValueChange={applyServiceSchedule}
          >
            <SelectTrigger className="h-12 bg-white" data-testid="service-schedule-select">
              <SelectValue placeholder="Select a service interval to auto-populate items..." />
            </SelectTrigger>
            <SelectContent>
              {serviceSchedules.map(schedule => (
                <SelectItem key={schedule.schedule_id} value={schedule.schedule_id}>
                  <div className="flex items-center justify-between w-full gap-4">
                    <span>{schedule.service_name}</span>
                    <span className="text-slate-500 text-sm">
                      ~${schedule.estimated_total?.toFixed(2)}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedSchedule && (
            <p className="text-xs text-blue-600 mt-2">
              {selectedSchedule.description} • {selectedSchedule.line_items?.length} items loaded
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="service_type">Service Type</Label>
          <Input
            id="service_type"
            value={invoiceForm.service_type}
            onChange={(e) => setInvoiceForm({...invoiceForm, service_type: e.target.value})}
            placeholder="e.g., General Service, Brake Repair"
            className="h-12"
            data-testid="service-type-input"
          />
        </div>
        <div>
          <Label htmlFor="odometer_reading">Odometer Reading (km)</Label>
          <Input
            id="odometer_reading"
            type="number"
            value={invoiceForm.odometer_reading}
            onChange={(e) => {
              setInvoiceForm({...invoiceForm, odometer_reading: e.target.value});
              // Refresh schedules based on new odometer
              if (e.target.value && parseInt(e.target.value) > 0) {
                fetchServiceSchedules(parseInt(e.target.value));
              }
            }}
            placeholder="e.g., 85000"
            className="h-12"
            data-testid="odometer-input"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={invoiceForm.status} onValueChange={(val) => setInvoiceForm({...invoiceForm, status: val})}>
          <SelectTrigger className="h-12" data-testid="status-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Line Items */}
      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-3">
          <Label className="text-base font-semibold">Line Items</Label>
          <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
            <Plus className="h-4 w-4 mr-1" /> Add Item
          </Button>
        </div>
        
        <div className="space-y-3">
          {invoiceForm.line_items.map((item, index) => (
            <LineItemRow
              key={item.id || index}
              item={item}
              index={index}
              onUpdate={updateLineItem}
              onRemove={removeLineItem}
              showRemove={invoiceForm.line_items.length > 1}
            />
          ))}
        </div>
        
        <div className="flex justify-end mt-4 pt-4 border-t">
          <div className="text-right">
            <p className="text-sm text-slate-500">Total (inc. GST)</p>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(calculateTotal())}</p>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={invoiceForm.notes}
          onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})}
          placeholder="Additional notes or comments"
          rows={3}
          data-testid="notes-input"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" className="flex-1 h-12 bg-blue-600 hover:bg-blue-700" data-testid="save-invoice-button">
          {submitText}
        </Button>
        <Button type="button" variant="outline" onClick={() => { 
          setShowEditDialog(false); 
          setEditingInvoice(null);
          resetForm(); 
        }} className="flex-1 h-12">
          Cancel
        </Button>
      </div>
    </form>
  );

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="vehicle-details-container">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(`/customers/${vehicle.customer_id}`)}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customer
          </Button>
          
          <div className="flex items-center gap-4 mb-2">
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
              <Car className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="font-['Chivo'] font-bold text-4xl text-slate-900" data-testid="vehicle-rego">
                {vehicle.rego || 'No Rego'}
              </h1>
              <p className="text-slate-500 text-lg">
                {[vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(' ') || 'Vehicle Details'}
              </p>
            </div>
          </div>
        </div>

        {/* Vehicle & Customer Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Car className="h-5 w-5 text-blue-600" />
              Vehicle Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Registration</p>
                <p className="font-medium text-slate-900">{vehicle.rego || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Color</p>
                <p className="font-medium text-slate-900">{vehicle.color || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Make</p>
                <p className="font-medium text-slate-900">{vehicle.make || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Model</p>
                <p className="font-medium text-slate-900">{vehicle.model || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Year</p>
                <p className="font-medium text-slate-900">{vehicle.year || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">VIN</p>
                <p className="font-medium text-slate-900">{vehicle.vin || '-'}</p>
              </div>
            </div>
          </Card>

          {customer && (
            <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Customer Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Name</p>
                  <p className="font-medium text-slate-900">{customer.display_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">{customer.email || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Phone</p>
                  <p className="font-medium text-slate-900">{customer.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Address</p>
                  <p className="font-medium text-slate-900">{customer.address || '-'}</p>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Service Actions Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Chivo'] font-bold text-2xl text-slate-900">
              Service Actions
            </h2>
            <div className="flex gap-3">
              <Dialog open={showJobCardDialog} onOpenChange={(open) => {
                setShowJobCardDialog(open);
                if (open) fetchMechanics();
              }}>
                <DialogTrigger asChild>
                  <Button className="h-12 px-6 bg-green-600 hover:bg-green-700" data-testid="create-job-card-button">
                    <Wrench className="h-5 w-5 mr-2" />
                    Create Service Job Card
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Service Job Card</DialogTitle>
                    <DialogDescription>
                      Create a new job card for {vehicle.rego} ({vehicle.make} {vehicle.model})
                    </DialogDescription>
                  </DialogHeader>
                  
                  <form onSubmit={handleCreateJobCard} className="space-y-4 pt-4">
                    {/* Vehicle Info Display */}
                    <Card className="bg-slate-50 p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Vehicle Rego</p>
                          <p className="font-semibold text-slate-900">{vehicle.rego || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Make & Model</p>
                          <p className="font-semibold text-slate-900">{[vehicle.make, vehicle.model].filter(Boolean).join(' ') || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Customer</p>
                          <p className="font-semibold text-slate-900">{customer?.display_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Phone</p>
                          <p className="font-semibold text-slate-900">{customer?.phone || '-'}</p>
                        </div>
                      </div>
                    </Card>
                    
                    {/* Service Type */}
                    <div>
                      <Label htmlFor="service_type">Service Type *</Label>
                      <Select 
                        value={jobCardForm.service_type} 
                        onValueChange={(val) => setJobCardForm({...jobCardForm, service_type: val})}
                      >
                        <SelectTrigger className="h-12 mt-1" data-testid="job-card-service-type">
                          <SelectValue placeholder="Select service type" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICE_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Scheduled Date */}
                    <div>
                      <Label htmlFor="scheduled_date">Scheduled Date *</Label>
                      <Input
                        id="scheduled_date"
                        type="date"
                        value={jobCardForm.scheduled_date}
                        onChange={(e) => setJobCardForm({...jobCardForm, scheduled_date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        className="h-12 mt-1"
                        data-testid="job-card-scheduled-date"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Select when this vehicle should be booked in for service
                      </p>
                    </div>
                    
                    {/* Priority */}
                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select 
                        value={jobCardForm.priority} 
                        onValueChange={(val) => setJobCardForm({...jobCardForm, priority: val})}
                      >
                        <SelectTrigger className="h-12 mt-1" data-testid="job-card-priority">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Assign Mechanic */}
                    <div>
                      <Label htmlFor="assigned_to">Assign Mechanic (Optional)</Label>
                      <Select 
                        value={jobCardForm.assigned_to || 'unassigned'} 
                        onValueChange={(val) => setJobCardForm({...jobCardForm, assigned_to: val === 'unassigned' ? '' : val})}
                      >
                        <SelectTrigger className="h-12 mt-1" data-testid="job-card-mechanic">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {mechanics.map(m => (
                            <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Comments */}
                    <div>
                      <Label htmlFor="comments">Comments / Notes</Label>
                      <Textarea
                        id="comments"
                        value={jobCardForm.comments}
                        onChange={(e) => setJobCardForm({...jobCardForm, comments: e.target.value})}
                        placeholder="Enter any additional notes or customer concerns..."
                        rows={3}
                        className="mt-1"
                        data-testid="job-card-comments"
                      />
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <Button 
                        type="submit" 
                        className="flex-1 h-12 bg-green-600 hover:bg-green-700" 
                        disabled={jobCardLoading}
                        data-testid="submit-job-card-button"
                      >
                        {jobCardLoading ? 'Creating...' : 'Create Job Card'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => { setShowJobCardDialog(false); resetJobCardForm(); }} 
                        className="flex-1 h-12"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        {/* Invoice History Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Chivo'] font-bold text-2xl text-slate-900">
              Invoice History ({invoices.length})
            </h2>
            <div className="flex gap-3">
              <Dialog open={showLinkDialog} onOpenChange={(open) => {
                setShowLinkDialog(open);
                if (open) fetchUnlinkedInvoices();
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="h-12" data-testid="link-invoice-button">
                    <LinkIcon className="h-5 w-5 mr-2" />
                    Link Old Invoice
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Link Existing Invoice</DialogTitle>
                    <DialogDescription>
                      Select an unlinked invoice from this customer to link it to this vehicle.
                    </DialogDescription>
                  </DialogHeader>
                  
                  {unlinkedInvoices.length === 0 ? (
                    <div className="py-8 text-center text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No unlinked invoices found for this customer.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {unlinkedInvoices.map((invoice) => (
                        <div 
                          key={invoice.invoice_id} 
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                          onClick={() => handleLinkInvoice(invoice.invoice_id)}
                        >
                          <div>
                            <p className="font-medium text-slate-900">{invoice.doc_no}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(invoice.invoice_date).toLocaleDateString('en-AU')} • {invoice.service_type || 'No service type'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-slate-900">{formatCurrency(invoice.total)}</p>
                            <Badge className={STATUS_COLORS[invoice.status]}>{invoice.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              <Button 
                className="h-12 px-6 bg-blue-600 hover:bg-blue-700" 
                data-testid="create-invoice-button"
                onClick={() => navigate(`/invoices/new?customerId=${customer?.customer_id}&vehicleId=${vehicleId}`)}
              >
                <Plus className="h-5 w-5 mr-2" />
                Create New Invoice
              </Button>
            </div>
          </div>

          <Card className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Doc No</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Service Type</TableHead>
                  <TableHead>Odometer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No invoices found</p>
                      <p className="text-slate-400 text-sm mt-1">Create your first invoice for this vehicle</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow 
                      key={invoice.invoice_id} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => openEditDialog(invoice)}
                      data-testid={`invoice-row-${invoice.invoice_id}`}
                    >
                      <TableCell className="font-medium text-blue-600 hover:text-blue-800 hover:underline" data-testid="invoice-doc-no">
                        {invoice.doc_no}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {new Date(invoice.invoice_date).toLocaleDateString('en-AU')}
                      </TableCell>
                      <TableCell className="text-slate-600">{invoice.service_type || '-'}</TableCell>
                      <TableCell className="text-slate-600">
                        {invoice.odometer_reading ? `${invoice.odometer_reading.toLocaleString()} km` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[invoice.status]}>{invoice.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">
                        {formatCurrency(invoice.total)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* Job Cards History Section */}
        <div className="mb-8">
          <Collapsible open={jobCardsExpanded} onOpenChange={setJobCardsExpanded}>
            <div className="flex items-center justify-between mb-4">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 p-0 hover:bg-transparent">
                  <h2 className="font-['Chivo'] font-bold text-2xl text-slate-900">
                    Service History ({jobCards.length})
                  </h2>
                  {jobCardsExpanded ? (
                    <ChevronUp className="h-5 w-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-500" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>

            <CollapsibleContent>
              {jobCardsLoading ? (
                <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
                  <p className="text-slate-500">Loading service history...</p>
                </Card>
              ) : jobCards.length === 0 ? (
                <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
                  <Wrench className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No service history found</p>
                  <p className="text-slate-400 text-sm mt-1">Create a job card to start tracking service history</p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {jobCards.map((jobCard) => (
                    <Card 
                      key={jobCard.job_card_id} 
                      className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden"
                      data-testid={`job-card-history-${jobCard.job_card_id}`}
                    >
                      {/* Job Card Header - Always Visible */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => toggleJobCardExpanded(jobCard.job_card_id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="flex flex-col">
                              <span className="font-semibold text-slate-900">{jobCard.job_card_number}</span>
                              <span className="text-sm text-slate-500">
                                {new Date(jobCard.booked_datetime).toLocaleDateString('en-AU', {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                            <Badge className={`${JOB_CARD_STATUS_COLORS[jobCard.status]} capitalize`}>
                              {JOB_CARD_STATUS_LABELS[jobCard.status] || jobCard.status}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {jobCard.service_type}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4">
                            {jobCard.assigned_mechanic_name && (
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <User className="h-4 w-4" />
                                {jobCard.assigned_mechanic_name}
                              </div>
                            )}
                            {expandedJobCards[jobCard.job_card_id] ? (
                              <ChevronUp className="h-5 w-5 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-slate-400" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedJobCards[jobCard.job_card_id] && (
                        <div className="border-t border-slate-100 bg-slate-50 p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Service Details */}
                            <div>
                              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                <Wrench className="h-4 w-4" />
                                Service Details
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Service Type:</span>
                                  <span className="font-medium capitalize">{jobCard.service_type}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Priority:</span>
                                  <span className="font-medium capitalize">{jobCard.priority}</span>
                                </div>
                                {jobCard.hours_worked && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Hours Worked:</span>
                                    <span className="font-medium">{jobCard.hours_worked} hrs</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Timeline */}
                            <div>
                              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                Timeline
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Booked:</span>
                                  <span className="font-medium">
                                    {new Date(jobCard.booked_datetime).toLocaleString('en-AU')}
                                  </span>
                                </div>
                                {jobCard.started_at && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Started:</span>
                                    <span className="font-medium">
                                      {new Date(jobCard.started_at).toLocaleString('en-AU')}
                                    </span>
                                  </div>
                                )}
                                {jobCard.completed_at && (
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Completed:</span>
                                    <span className="font-medium text-green-600">
                                      {new Date(jobCard.completed_at).toLocaleString('en-AU')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Comments / Customer Notes */}
                            {jobCard.comments && (
                              <div className="md:col-span-2">
                                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                  <MessageSquare className="h-4 w-4" />
                                  Customer Notes
                                </h4>
                                <p className="text-sm text-slate-600 bg-white rounded-lg p-3 border border-slate-200">
                                  {jobCard.comments}
                                </p>
                              </div>
                            )}

                            {/* Mechanic Findings */}
                            {jobCard.findings && (
                              <div className="md:col-span-2">
                                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                  <AlertCircle className="h-4 w-4 text-orange-500" />
                                  Mechanic Findings
                                </h4>
                                <p className="text-sm text-slate-600 bg-orange-50 rounded-lg p-3 border border-orange-200">
                                  {jobCard.findings}
                                </p>
                              </div>
                            )}

                            {/* Work Performed */}
                            {jobCard.work_performed && (
                              <div className="md:col-span-2">
                                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                                  Work Performed
                                </h4>
                                <p className="text-sm text-slate-600 bg-green-50 rounded-lg p-3 border border-green-200">
                                  {jobCard.work_performed}
                                </p>
                              </div>
                            )}

                            {/* Quality Checklist */}
                            {jobCard.quality_checklist && (
                              <div className="md:col-span-2">
                                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                  <CheckCircle2 className="h-4 w-4" />
                                  Quality Checklist
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                  {[
                                    { key: 'repairs_checked', label: 'Repairs' },
                                    { key: 'brakes_checked', label: 'Brakes' },
                                    { key: 'tyres_checked', label: 'Tyres' },
                                    { key: 'wheel_alignment_checked', label: 'Wheel Alignment' },
                                    { key: 'fluids_topped', label: 'Fluids' },
                                    { key: 'engine_oil_checked', label: 'Engine Oil' }
                                  ].map(item => (
                                    <div key={item.key} className="flex items-center gap-2">
                                      {jobCard.quality_checklist[item.key] ? (
                                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                                      ) : (
                                        <div className="h-4 w-4 rounded-full border-2 border-slate-300" />
                                      )}
                                      <span className={jobCard.quality_checklist[item.key] ? 'text-green-700' : 'text-slate-400'}>
                                        {item.label}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                {jobCard.quality_checklist.next_service_date && (
                                  <p className="text-sm text-slate-600 mt-2">
                                    <span className="font-medium">Next Service:</span>{' '}
                                    {new Date(jobCard.quality_checklist.next_service_date).toLocaleDateString('en-AU')}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Photos */}
                            {jobCard.photos && jobCard.photos.length > 0 && (
                              <div className="md:col-span-2">
                                <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                  <Camera className="h-4 w-4" />
                                  Photos ({jobCard.photos.length})
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {jobCard.photos.map((photo, idx) => (
                                    <a 
                                      key={idx}
                                      href={photo.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="h-20 w-20 rounded-lg overflow-hidden border border-slate-200 hover:border-blue-400 transition-colors"
                                    >
                                      <img 
                                        src={photo.url} 
                                        alt={photo.filename}
                                        className="h-full w-full object-cover"
                                      />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* View Job Card Button */}
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate('/job-cards')}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              View in Job Cards
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>

      {/* Edit Invoice Sheet */}
      <Sheet open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) {
          setEditingInvoice(null);
          resetForm();
        }
      }}>
        <SheetContent side="right" className="w-full sm:max-w-4xl lg:max-w-5xl overflow-y-auto p-6">
          <SheetHeader className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle className="text-2xl">Edit Invoice {editingInvoice?.doc_no}</SheetTitle>
                <SheetDescription>
                  Update invoice details and line items
                </SheetDescription>
              </div>
              {editingInvoice && (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      // Open window immediately to avoid popup blocker
                      const printWindow = window.open('', '_blank');
                      if (!printWindow) {
                        toast.error('Please allow popups to print invoices');
                        return;
                      }
                      printWindow.document.write('<html><head><title>Loading Invoice...</title></head><body style="font-family: Arial; padding: 40px; text-align: center;"><h2>Loading Invoice...</h2></body></html>');
                      
                      try {
                        const response = await fetch(`${BACKEND_URL}/api/invoices/${editingInvoice.invoice_id}/print`, {
                          credentials: 'include'
                        });
                        if (!response.ok) throw new Error('Failed to load invoice');
                        const htmlContent = await response.text();
                        printWindow.document.open();
                        printWindow.document.write(htmlContent);
                        printWindow.document.close();
                      } catch (err) {
                        printWindow.document.write('<html><body style="padding: 40px;"><h2>Error loading invoice</h2></body></html>');
                        toast.error('Failed to generate invoice');
                      }
                    }}
                    data-testid="sheet-print-btn"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={guardComms(async () => {
                      try {
                        const response = await fetch(`${BACKEND_URL}/api/invoices/${editingInvoice.invoice_id}/send-email`, {
                          method: 'POST',
                          credentials: 'include'
                        });
                        if (response.ok) {
                          toast.success('Invoice emailed successfully');
                        } else {
                          const error = await response.json();
                          toast.error(error.detail || 'Failed to send email');
                        }
                      } catch (err) {
                        toast.error('Failed to send email');
                      }
                    })}
                    data-testid="sheet-email-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={guardComms(() => setShowSmsModal(true))}
                    data-testid="sheet-sms-btn"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    SMS
                  </Button>
                </div>
              )}
            </div>
          </SheetHeader>
          <InvoiceForm
            vehicle={vehicle}
            customer={customer}
            serviceSchedules={serviceSchedules}
            initialData={editingInvoice ? {
              service_type: editingInvoice.service_type || '',
              odometer_reading: editingInvoice.odometer_reading || '',
              notes: editingInvoice.notes || '',
              status: editingInvoice.status || 'draft',
              line_items: editingInvoice.line_items || [],
              service_categories: editingInvoice.service_categories || []
            } : null}
            onSubmit={handleUpdateInvoice}
            onCancel={() => {
              setShowEditDialog(false);
              setEditingInvoice(null);
              resetForm();
            }}
            submitText="Update Invoice"
          />
        </SheetContent>
      </Sheet>

      {/* SMS Template Modal */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Send Invoice via SMS
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-slate-700">Sending to:</p>
              <p className="text-slate-600">{customer?.display_name || customer?.first_name} - {customer?.phone}</p>
            </div>

            {smsTemplates.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select Template</Label>
                <Select value={selectedSmsTemplate} onValueChange={(val) => {
                  setSelectedSmsTemplate(val);
                  setCustomSmsMessage('');
                }}>
                  <SelectTrigger data-testid="vehicle-sms-template-select">
                    <SelectValue placeholder="Choose a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {smsTemplates.map(template => (
                      <SelectItem key={template.template_id} value={template.template_id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedSmsTemplate && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <p className="text-blue-700">
                      {smsTemplates.find(t => t.template_id === selectedSmsTemplate)?.content
                        .replace(/{customer_name}/g, customer?.display_name || customer?.first_name || 'Customer')
                        .replace(/{vehicle_rego}/g, vehicle?.rego || '')
                        .replace(/{invoice_total}/g, `$${editingInvoice?.total?.toFixed(2) || '0.00'}`)
                        .replace(/{invoice_id}/g, editingInvoice?.doc_no || '')}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {smsTemplates.length > 0 ? 'Or write a custom message' : 'Message'}
              </Label>
              <Textarea
                value={customSmsMessage}
                onChange={(e) => {
                  setCustomSmsMessage(e.target.value);
                  setSelectedSmsTemplate('');
                }}
                placeholder="Type your message here..."
                rows={4}
                className="resize-none"
                data-testid="vehicle-sms-custom-message"
              />
              <p className="text-xs text-slate-500">
                Available placeholders: {'{customer_name}'}, {'{vehicle_rego}'}, {'{invoice_total}'}, {'{invoice_id}'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSmsModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendSms} 
              disabled={sendingSms || (!selectedSmsTemplate && !customSmsMessage.trim())}
              data-testid="vehicle-send-sms-confirm-btn"
            >
              {sendingSms ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {commsDialog}
    </Layout>
  );
}
